// utils/adminAuth.js
// Admin portal session: a separate short-lived token obtained by re-confirming an
// emailed code (see /api/v1/admin/session/*). Kept in sessionStorage so it ends
// with the tab and is never sent to customer endpoints.
import { jwtDecode } from "jwt-decode";
import { reportServerVersion } from "./serverVersion";

const KEY = "adminToken";
export const ADMIN_SESSION_ENDED = "ez-admin-session-ended";

export const getAdminToken = () => {
  try { return sessionStorage.getItem(KEY); } catch { return null; }
};

export const setAdminToken = (token) => {
  try { sessionStorage.setItem(KEY, token); } catch { /* storage unavailable: session lasts for this page only */ }
};

export const clearAdminToken = () => {
  try { sessionStorage.removeItem(KEY); } catch { /* nothing stored */ }
};

/** Expiry of the admin token in ms since epoch, or null. */
export const adminTokenExpiry = () => {
  const token = getAdminToken();
  if (!token) return null;
  try { return jwtDecode(token).exp * 1000; } catch { return null; }
};

export const hasValidAdminToken = () => {
  const exp = adminTokenExpiry();
  return exp !== null && exp > Date.now() + 30 * 1000;
};

export const endAdminSession = () => {
  clearAdminToken();
  window.dispatchEvent(new Event(ADMIN_SESSION_ENDED));
};

/** fetch() with the admin token. Ends the admin session when the server says it is gone. */
export const adminFetch = async (url, options = {}) => {
  const token = getAdminToken();
  if (!token) {
    endAdminSession();
    throw new Error("Admin session required");
  }

  const response = await fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
  });
  reportServerVersion(response);

  if (response.status === 401) {
    endAdminSession();
  } else if (response.status === 403) {
    // 403 is usually "you may not do this" — only a missing/invalid session ends it
    const body = await response.clone().json().catch(() => ({}));
    if (body.error === "Admin session required") endAdminSession();
  }
  return response;
};

/** adminFetch + JSON body; throws an Error carrying .status and the server's message. */
export const adminJson = async (url, { method = "GET", body } = {}) => {
  const response = await adminFetch(url, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || `HTTP ${response.status}`);
    error.status = response.status;
    error.errors = data.errors; // per-field messages (wizard validation)
    throw error;
  }
  return data;
};
